/**
 * ShipmentDetailsPage — full view + sender actions.
 * Mirrors Flutter `shipment_details_page.dart`. Auto-refreshes every 20s
 * while the shipment is in a non-terminal state (handled by useShipment).
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Phone, MapPin, Package, Hash, Loader, AlertCircle,
  Truck, CheckCircle, XCircle, Flag, User, DollarSign, Building2,
} from 'lucide-react';
import {
  useShipment,
  useCancelShipment,
  useApproveClaim,
  useRejectClaim,
} from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { StatusBadge } from '@/components/shipments/StatusBadge';
import { Button } from '@/components/common/Button';
import { isTerminal } from '@/types/shipment';

export default function ShipmentDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const idNum = id ? parseInt(id, 10) : null;
  const { data: s, isLoading, isError, refetch } = useShipment(idNum);
  const cancelMut = useCancelShipment();
  const approveMut = useApproveClaim();
  const rejectMut = useRejectClaim();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (isError || !s) {
    return (
      <AppLayout>
        <div className="bg-error/10 text-error rounded-xl p-4 text-sm flex items-center justify-between">
          <span>{t('shipment_not_found', 'الشحنة غير موجودة')}</span>
          <button onClick={() => refetch()} className="underline">
            {t('retry', 'إعادة المحاولة')}
          </button>
        </div>
      </AppLayout>
    );
  }

  const isAwaitingApproval =
    s.status === 'awaiting_approval' || s.status === 'awaiting_sender_approval';
  const canCancel = !isTerminal(s.status) && s.status !== 'in_transit' && s.status !== 'out_for_delivery';
  const canFindDrivers = s.status === 'pending';

  async function doApprove() {
    setActionError(null);
    try { await approveMut.mutateAsync(s!.id); }
    catch (e: any) { setActionError(e?.message || 'خطأ غير متوقّع'); }
  }
  async function doReject() {
    setActionError(null);
    try { await rejectMut.mutateAsync({ id: s!.id }); }
    catch (e: any) { setActionError(e?.message || 'خطأ غير متوقّع'); }
  }
  async function doCancel() {
    setActionError(null);
    try {
      await cancelMut.mutateAsync({ id: s!.id });
      setConfirmCancel(false);
      navigate('/shipments', { replace: true });
    } catch (e: any) { setActionError(e?.message || 'خطأ غير متوقّع'); }
  }

  const price = s.final_price ?? s.price ?? s.estimated_price;
  const offered = s.offered_price;

  return (
    <AppLayout>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3 text-text-secondary">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-background rounded">
          <ArrowRight size={20} />
        </button>
        <span className="text-sm">{t('back', 'رجوع')}</span>
      </div>

      <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-mono text-primary">
            <Hash size={14} /> {s.tracking_number}
          </div>
          <StatusBadge status={s.status} size="md" />
        </div>
        <div className="text-sm text-text-secondary">
          {s.short_code && (
            <span>
              {t('short_code', 'الكود المختصر')}:{' '}
              <span className="font-mono font-bold text-text-primary">{s.short_code}</span>
            </span>
          )}
        </div>
      </div>

      {/* Center attribution banner — shown when this shipment was created
          on behalf of the customer by a partner center (migration 163).
          Lets the customer confirm at a glance which center filled the
          form for them and when they consented. */}
      {(s as any).created_by_center && (
        <div className="bg-info/5 border border-info/30 rounded-2xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="bg-info/10 text-info rounded-full p-2 flex-shrink-0">
              <Building2 size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-text-primary text-sm mb-1">
                {t('shipment_created_by_center', 'هذه الشحنة أنشأها مركز الشحن بموافقتك')}
              </div>
              <div className="text-sm text-text-primary">
                {(s as any).created_by_center.name}
              </div>
              {(s as any).created_by_center.city && (
                <div className="text-xs text-text-tertiary flex items-center gap-1 mt-0.5">
                  <MapPin size={11} /> {(s as any).created_by_center.city}
                  {(s as any).created_by_center.country && ` · ${(s as any).created_by_center.country}`}
                </div>
              )}
              {(s as any).created_by_center.phone && (
                <div className="text-xs text-text-tertiary flex items-center gap-1 mt-0.5">
                  <Phone size={11} /> <span dir="ltr">{(s as any).created_by_center.phone}</span>
                </div>
              )}
              {(s as any).center_consent_at && (
                <div className="text-xs text-text-tertiary mt-1">
                  {t('consented_at', 'وافقتَ بتاريخ')}: {new Date((s as any).center_consent_at).toLocaleString('ar-SY')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action error */}
      {actionError && (
        <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4 flex items-center gap-2">
          <AlertCircle size={16} /> {actionError}
        </div>
      )}

      {/* Awaiting approval — most important CTA */}
      {isAwaitingApproval && (
        <div className="bg-warning/10 border border-warning/30 rounded-2xl p-5 mb-4">
          <div className="font-bold text-text-primary mb-2 flex items-center gap-2">
            <Truck size={18} /> {t('driver_claimed', 'سائق قَبِل شحنتك')}
          </div>
          {s.driver_name && (
            <div className="text-sm text-text-secondary mb-1">
              {t('driver', 'السائق')}: {s.driver_name}
            </div>
          )}
          {offered != null && (
            <div className="text-sm text-text-secondary mb-3">
              {t('offered_price', 'السعر المعروض')}:{' '}
              <span className="font-bold text-secondary">{offered} {s.currency || 'USD'}</span>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={doApprove} loading={approveMut.isPending}>
              <CheckCircle size={16} /> {t('approve', 'موافقة')}
            </Button>
            <Button variant="ghost" onClick={doReject} loading={rejectMut.isPending}>
              <XCircle size={16} /> {t('reject', 'رفض')}
            </Button>
          </div>
        </div>
      )}

      {/* Receiver & route */}
      <div className="bg-surface border border-border rounded-2xl p-5 mb-4 space-y-3">
        <h3 className="font-bold text-text-primary mb-2">{t('details', 'التفاصيل')}</h3>
        <Row icon={<User size={16} />} label={t('receiver', 'المستلِم')} value={s.receiver_name} />
        <Row icon={<Phone size={16} />} label={t('phone', 'الهاتف')} value={s.receiver_phone} ltr />
        <Row icon={<MapPin size={16} />} label={t('from', 'من')} value={s.pickup_city || s.origin_city || '—'} />
        <Row icon={<MapPin size={16} className="text-secondary" />} label={t('to', 'إلى')} value={s.receiver_city || s.destination_city || '—'} />
        <Row icon={<Package size={16} />} label={t('weight', 'الوزن')} value={`${s.weight} كغ`} />
        {s.package_description && (
          <Row icon={<Package size={16} />} label={t('description', 'الوصف')} value={s.package_description} />
        )}
        {price != null && (
          <Row
            icon={<DollarSign size={16} />}
            label={t('price', 'السعر')}
            value={<span className="font-bold text-secondary">{price} {s.currency || 'USD'}</span>}
          />
        )}
      </div>

      {/* Driver info */}
      {s.driver_name && !isAwaitingApproval && (
        <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
          <h3 className="font-bold text-text-primary mb-3">{t('driver', 'السائق')}</h3>
          <Row icon={<User size={16} />} label={t('name', 'الاسم')} value={s.driver_name} />
          {s.plate_number && (
            <Row icon={<Truck size={16} />} label={t('vehicle', 'المركبة')} value={`${s.vehicle_brand || ''} ${s.vehicle_model || ''} — ${s.plate_number}`} />
          )}
        </div>
      )}

      {/* Driver-side delivery-receipt CTAs — visible when shipment is in
          the driver's care. Server enforces who can call each endpoint;
          we light up the right button at the right phase. */}
      {(s.status === 'accepted') && (
        <div className="bg-primary/5 border border-primary/30 rounded-2xl p-4 mb-4">
          <div className="font-bold text-primary mb-2 flex items-center gap-2">
            <Package size={16} /> {t('driver_next_pickup', 'الخطوة التالية: استلام الكرتون من المرسِل')}
          </div>
          <Button onClick={() => navigate(`/shipments/${s.id}/pickup`)} fullWidth>
            <Package size={16} /> {t('go_to_pickup', 'تأكيد الاستلام بإيصال')}
          </Button>
        </div>
      )}
      {(s.status === 'picked_up' || s.status === 'in_transit' || s.status === 'out_for_delivery' || s.status === 'at_center') && (
        <div className="bg-success/5 border border-success/30 rounded-2xl p-4 mb-4">
          <div className="font-bold text-success mb-2 flex items-center gap-2">
            <CheckCircle size={16} /> {t('driver_next_deliver', 'الخطوة التالية: تسليم للمستلم')}
          </div>
          <Button onClick={() => navigate(`/shipments/${s.id}/deliver`)} fullWidth>
            <CheckCircle size={16} /> {t('go_to_deliver', 'تأكيد التسليم بإيصال')}
          </Button>
        </div>
      )}

      {/* Sender / general actions */}
      <div className="flex flex-wrap gap-2">
        {canFindDrivers && (
          <Button onClick={() => navigate(`/shipments/${s.id}/find-driver`)}>
            <Truck size={16} /> {t('find_driver', 'البحث عن سائق')}
          </Button>
        )}
        {!isTerminal(s.status) && (
          <Button variant="ghost" onClick={() => navigate(`/track/${encodeURIComponent(s.tracking_number)}`)}>
            <MapPin size={16} /> {t('track', 'تتبّع')}
          </Button>
        )}
        {canCancel && (
          <Button
            variant="ghost"
            className="!border-error !text-error hover:!bg-error/10"
            onClick={() => setConfirmCancel(true)}
          >
            <XCircle size={16} /> {t('cancel_shipment', 'إلغاء الشحنة')}
          </Button>
        )}
        <Button variant="ghost" onClick={() => alert(t('report_coming_soon', 'الإبلاغ قريباً'))}>
          <Flag size={16} /> {t('report', 'إبلاغ')}
        </Button>
      </div>

      {/* Cancel confirm modal */}
      {confirmCancel && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50"
          onClick={() => setConfirmCancel(false)}
        >
          <div
            className="bg-surface rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-text-primary mb-2">{t('confirm_cancel_title', 'تأكيد الإلغاء')}</h3>
            <p className="text-text-secondary text-sm mb-5">
              {t('confirm_cancel_body', 'هل أنت متأكد من إلغاء الشحنة؟ لا يمكن التراجع.')}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setConfirmCancel(false)}>
                {t('back', 'رجوع')}
              </Button>
              <Button
                onClick={doCancel}
                loading={cancelMut.isPending}
                className="!bg-error hover:!bg-error/90"
              >
                {t('confirm_cancel', 'تأكيد الإلغاء')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

function Row({ icon, label, value, ltr }: { icon: React.ReactNode; label: string; value: React.ReactNode; ltr?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="flex items-center gap-2 text-text-secondary">{icon}{label}</span>
      <span className="text-text-primary text-right" dir={ltr ? 'ltr' : undefined}>{value}</span>
    </div>
  );
}
